import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    Alert,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    TextField
} from "@mui/material";
import { AddPhotoAlternate, Close } from "@mui/icons-material";
// Commented out color import as field is hidden
// import { colors } from "../../../data/filter/color";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { createProduct } from "../../../State/seller/sellerProductSlice";
import { fetchAllCategories } from "../../../State/customer/categorySlice"; 
import { uploadToLocalhost } from "../../../config/uploadToLocalhost";

// Commented out sizesList as selection UI is hidden
// const sizesList = ["FREE", "S", "M", "L", "XL", "XXL"];

// Form Validation Schema
const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    mrpPrice: Yup.number().positive("MRP must be greater than 0").required("MRP Price is required"),
    sellingPrice: Yup.number()
        .positive("Selling price must be greater than 0")
        .lessThan(Yup.ref("mrpPrice"), "Selling price must be lower than MRP")
        .required("Selling Price is required"),
    quantity: Yup.number().integer().min(0, "Quantity cannot be negative").required("Quantity is required"),
    /* Color and Size validation disabled since fields are hidden and auto-filled on submit */
    // color: Yup.string().required("Color selection is required"),
    // sizes: Yup.string().required("Size selection is required"),
    category: Yup.string().required("Main category is required"),
    category2: Yup.string().required("Sub category is required"),
    category3: Yup.string().required("Child category is required"),
    images: Yup.array().min(1, "At least one product image is required"),
});

const AddProduct = () => {
    const [uploadingImage, setUploadingImage] = useState(false);
    const [snackbarOpen, setOpenSnackbar] = useState(false);

    const dispatch = useAppDispatch();

    const { categories } = useAppSelector((state) => state.category);
    const { loading: submitting } = useAppSelector((state) => state.sellerProduct);

    useEffect(() => {
        dispatch(fetchAllCategories());
    }, [dispatch]);

    const formik = useFormik({
        initialValues: {
            title: "",
            description: "",
            mrpPrice: "",
            sellingPrice: "",
            quantity: "",
            color: "",
            images: [] as string[],
            category: "",
            category2: "",
            category3: "",
            sizes: "",
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            const formattedPayload = {
                ...values,
                color: "BLACK", // Hardcoded default color
                sizes: "FREE",   // Hardcoded default size
                mrpPrice: Number(values.mrpPrice),
                sellingPrice: Number(values.sellingPrice),
                quantity: Number(values.quantity),
            };

            const resultAction = await dispatch(
                createProduct({
                    request: formattedPayload,
                    jwt: localStorage.getItem("jwt"),
                })
            );

            if (createProduct.fulfilled.match(resultAction)) {
                setOpenSnackbar(true);
                resetForm();
            }
        },
    });

    const handleMainCategoryChange = (e: any) => {
        const value = e.target.value;
        formik.setFieldValue("category", value);
        formik.setFieldValue("category2", "");
        formik.setFieldValue("category3", "");
    };

    const handleCategory2Change = (e: any) => {
        const value = e.target.value;
        formik.setFieldValue("category2", value);
        formik.setFieldValue("category3", "");
    };

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadingImage(true);
        try {
            const imageUrl = await uploadToLocalhost(file);
            if (imageUrl) {
                formik.setFieldValue("images", [...formik.values.images, imageUrl]);
            }
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setUploadingImage(false);
            event.target.value = "";
        }
    };

    const handleRemoveImage = (index: number) => {
        const updatedImages = [...formik.values.images];
        updatedImages.splice(index, 1);
        formik.setFieldValue("images", updatedImages);
    };

    const handleCloseSnackbar = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") return;
        setOpenSnackbar(false);
    };

    // Category Level Logic
    const mainCategories = categories?.filter((cat: any) => cat.level === 1) || [];

    const subCategories = categories?.filter((cat: any) => {
        if (!formik.values.category) return false;
        const parentId = cat.parentCategoryId ?? cat.parentCategory?.categoryId;
        return String(parentId) === String(formik.values.category);
    }) || [];

    const childCategories = categories?.filter((cat: any) => {
        if (!formik.values.category2) return false;
        const parentId = cat.parentCategoryId ?? cat.parentCategory?.categoryId;
        return String(parentId) === String(formik.values.category2);
    }) || [];

    return (
        <div>
            <form onSubmit={formik.handleSubmit} className="space-y-4 p-4">
                <Grid container spacing={2}>
                    {/* Image Upload */}
                    <Grid size={{ xs: 12 }} className="flex flex-wrap gap-5">
                        <input
                            type="file"
                            accept="image/*"
                            id="fileInput"
                            style={{ display: "none" }}
                            onChange={handleImageChange}
                        />

                        <label className="relative" htmlFor="fileInput">
                            <span className="w-24 h-24 cursor-pointer flex items-center justify-center p-3 border rounded-md border-gray-400">
                                <AddPhotoAlternate className="text-gray-700" />
                            </span>
                            {uploadingImage && (
                                <div className="absolute inset-0 w-24 h-24 flex justify-center items-center bg-white/60">
                                    <CircularProgress size={24} />
                                </div>
                            )}
                        </label>

                        <div className="flex flex-wrap gap-2">
                            {formik.values.images.map((image, index) => (
                                <div key={index} className="relative">
                                    <img
                                        className="w-24 h-24 object-cover rounded-md"
                                        src={image}
                                        alt={`productImage ${index + 1}`}
                                    />
                                    <IconButton
                                        onClick={() => handleRemoveImage(index)}
                                        size="small"
                                        color="error"
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            backgroundColor: "rgba(255,255,255,0.7)",
                                        }}
                                    >
                                        <Close sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                        {formik.touched.images && formik.errors.images && (
                            <FormHelperText error>{formik.errors.images as string}</FormHelperText>
                        )}
                    </Grid>

                    {/* Title */}
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            id="title"
                            name="title"
                            label="Title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.title && Boolean(formik.errors.title)}
                            helperText={formik.touched.title && formik.errors.title}
                            required
                        />
                    </Grid>

                    {/* Description */}
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            multiline
                            rows={4}
                            fullWidth
                            id="description"
                            name="description"
                            label="Description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.description && Boolean(formik.errors.description)}
                            helperText={formik.touched.description && formik.errors.description}
                            required
                        />
                    </Grid>

                    {/* MRP Price */}
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <TextField
                            fullWidth
                            id="mrpPrice"
                            name="mrpPrice"
                            label="MRP Price"
                            type="number"
                            value={formik.values.mrpPrice}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.mrpPrice && Boolean(formik.errors.mrpPrice)}
                            helperText={formik.touched.mrpPrice && formik.errors.mrpPrice}
                            required
                        />
                    </Grid>

                    {/* Selling Price */}
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <TextField
                            fullWidth
                            id="sellingPrice"
                            name="sellingPrice"
                            label="Selling Price"
                            type="number"
                            value={formik.values.sellingPrice}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.sellingPrice && Boolean(formik.errors.sellingPrice)}
                            helperText={formik.touched.sellingPrice && formik.errors.sellingPrice}
                            required
                        />
                    </Grid>

                    {/* Color Dropdown (Commented out for later re-use) */}
                    {/* 
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <FormControl
                            fullWidth
                            error={formik.touched.color && Boolean(formik.errors.color)}
                            required
                        >
                            <InputLabel id="color-label">Color</InputLabel>
                            <Select
                                labelId="color-label"
                                id="color"
                                name="color"
                                value={formik.values.color}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label="Color"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {colors.map((color, index) => (
                                    <MenuItem key={index} value={color.name}>
                                        <div className="flex gap-3 items-center">
                                            <span
                                                style={{ backgroundColor: color.hex }}
                                                className={`h-5 w-5 rounded-full ${color.name === "White" ? "border" : ""}`}
                                            />
                                            <p>{color.name}</p>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.color && formik.errors.color && (
                                <FormHelperText>{formik.errors.color}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid> 
                    */}

                    {/* Sizes Dropdown (Commented out for later re-use) */}
                    {/* 
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <FormControl
                            fullWidth
                            error={formik.touched.sizes && Boolean(formik.errors.sizes)}
                            required
                        >
                            <InputLabel id="sizes-label">Sizes</InputLabel>
                            <Select
                                labelId="sizes-label"
                                id="sizes"
                                name="sizes"
                                value={formik.values.sizes}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label="Sizes"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {sizesList.map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.sizes && formik.errors.sizes && (
                                <FormHelperText>{formik.errors.sizes}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid> 
                    */}

                    {/* Quantity Input */}
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <TextField
                            fullWidth
                            id="quantity"
                            name="quantity"
                            label="Quantity"
                            type="number"
                            value={formik.values.quantity}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                            helperText={formik.touched.quantity && formik.errors.quantity}
                            required
                        />
                    </Grid>

                    {/* Level 1 Category */}
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <FormControl
                            fullWidth
                            required
                            error={formik.touched.category && Boolean(formik.errors.category)}
                        >
                            <InputLabel id="category-label">Main Category</InputLabel>
                            <Select
                                labelId="category-label"
                                id="category"
                                name="category"
                                value={formik.values.category}
                                onChange={handleMainCategoryChange}
                                label="Main Category"
                            >
                                {mainCategories.map((cat: any) => (
                                    <MenuItem key={cat.categoryId} value={String(cat.categoryId)}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.category && formik.errors.category && (
                                <FormHelperText>{formik.errors.category}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Level 2 Category */}
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <FormControl
                            fullWidth
                            required
                            disabled={!formik.values.category}
                            error={formik.touched.category2 && Boolean(formik.errors.category2)}
                        >
                            <InputLabel id="category2-label">Sub Category</InputLabel>
                            <Select
                                labelId="category2-label"
                                id="category2"
                                name="category2"
                                value={formik.values.category2}
                                onChange={handleCategory2Change}
                                label="Sub Category"
                            >
                                {subCategories.map((cat: any) => (
                                    <MenuItem key={cat.categoryId} value={String(cat.categoryId)}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.category2 && formik.errors.category2 && (
                                <FormHelperText>{formik.errors.category2}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Level 3 Category */}
                    <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <FormControl
                            fullWidth
                            required
                            disabled={!formik.values.category2}
                            error={formik.touched.category3 && Boolean(formik.errors.category3)}
                        >
                            <InputLabel id="category3-label">Child Category</InputLabel>
                            <Select
                                labelId="category3-label"
                                id="category3"
                                name="category3"
                                value={formik.values.category3}
                                onChange={formik.handleChange}
                                label="Child Category"
                            >
                                {childCategories.map((cat: any) => (
                                    <MenuItem key={cat.categoryId} value={String(cat.categoryId)}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.category3 && formik.errors.category3 && (
                                <FormHelperText>{formik.errors.category3}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Submit Button */}
                    <Grid size={{ xs: 12 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={submitting}
                            sx={{ backgroundColor: "#DE8F07", "&:hover": { backgroundColor: "#c47d06" } }}
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : "Add Product"}
                        </Button>
                    </Grid>
                </Grid>
            </form>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
                    Product added successfully!
                </Alert>
            </Snackbar>
        </div>
    );
};

export default AddProduct;